'use client';

import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    useMemo,
} from 'react';
import {
    CONSENT_VERSION,
    loadConsent,
    persistConsent,
    clearConsent,
    type ConsentState,
    type Categories,
    type ConsentCategory,
} from '@/lib/consent';

export type ConsentStatus = 'loading' | 'pending' | 'granted';

interface ConsentContextValue {
    /** `loading` until the first client read resolves (prevents hydration mismatch). */
    status: ConsentStatus;
    consent: ConsentState | null;
    /** Whether the granular preferences modal is currently open. */
    preferencesOpen: boolean;
    /** Record a decision (necessary is always forced true) and persist it. */
    decide: (categories: Categories) => void;
    openPreferences: () => void;
    closePreferences: () => void;
    /** Withdraw all non-essential consent and re-prompt. */
    resetConsent: () => void;
    /** True when the given category has been consented to. */
    has: (category: ConsentCategory) => boolean;
}

const ConsentContext = createContext<ConsentContextValue | undefined>(undefined);

export function ConsentProvider({ children }: { children: React.ReactNode }) {
    const [status, setStatus] = useState<ConsentStatus>('loading');
    const [consent, setConsent] = useState<ConsentState | null>(null);
    const [preferencesOpen, setPreferencesOpen] = useState(false);

    // Read any stored decision ONLY after mount. During SSR and the first
    // client paint `status === 'loading'`, so consumers render nothing and the
    // server/client markup stay identical.
    useEffect(() => {
        const stored = loadConsent();
        if (stored && stored.version >= CONSENT_VERSION) {
            setConsent(stored);
            setStatus('granted');
        } else {
            setStatus('pending');
        }
    }, []);

    const decide = useCallback((categories: Categories) => {
        const next: ConsentState = {
            version: CONSENT_VERSION,
            categories: { ...categories, necessary: true },
            decidedAt: new Date().toISOString(),
        };
        setConsent(next);
        setStatus('granted');
        setPreferencesOpen(false);
        // Best-effort persistence; blocked storage never throws here.
        persistConsent(next);
    }, []);

    const openPreferences = useCallback(() => setPreferencesOpen(true), []);
    const closePreferences = useCallback(() => setPreferencesOpen(false), []);

    const resetConsent = useCallback(() => {
        clearConsent();
        setConsent(null);
        setStatus('pending');
        setPreferencesOpen(false);
    }, []);

    const has = useCallback(
        (category: ConsentCategory) => consent?.categories[category] === true,
        [consent],
    );

    const value = useMemo<ConsentContextValue>(
        () => ({
            status,
            consent,
            preferencesOpen,
            decide,
            openPreferences,
            closePreferences,
            resetConsent,
            has,
        }),
        [status, consent, preferencesOpen, decide, openPreferences, closePreferences, resetConsent, has],
    );

    return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent(): ConsentContextValue {
    const ctx = useContext(ConsentContext);
    if (ctx === undefined) {
        throw new Error('useConsent must be used within a ConsentProvider');
    }
    return ctx;
}
